import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './WorkoutRoutine.css';

// ── GCS image URL helper ──────────────────────────────────────────────────────

const GCS = 'https://storage.googleapis.com/mosaime-workout-media/exercises';

function pose(slug: string): string[] {
  return [`${GCS}/${slug}/pose.png`];
}

// ── Static yoga workout data ──────────────────────────────────────────────────

interface Exercise {
  id: string;
  name: string;
  description: string;
  muscles: string[];
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  images: string[];
  tips: string[];
  duration?: number;  // seconds
  rest?: number;
  isCardio: false;
}

const YOGA_EXERCISES: Exercise[] = [
  {
    id: 'cat-cow',
    name: 'Cat-Cow',
    description: 'Start on hands and knees. Inhale and drop your belly toward the mat, lifting your head and tailbone (cow). Exhale and round your spine toward the ceiling, tucking chin and tailbone (cat). Flow between the two.',
    muscles: ['Spinal Erectors', 'Core', 'Hip Flexors'],
    difficulty: 'Beginner',
    images: pose('cat-cow'),
    tips: ['Sync movement with breath', 'Full spinal range of motion', 'Shoulders over wrists', 'Hips over knees'],
    isCardio: false,
  },
  {
    id: 'child-s-pose',
    name: "Child's Pose",
    description: 'Kneel on the mat, then sit your hips back toward your heels and extend your arms forward along the mat. Rest your forehead down and breathe deeply into your back body.',
    muscles: ['Lower Back', 'Hips', 'Glutes', 'Shoulders'],
    difficulty: 'Beginner',
    images: pose('child-s-pose'),
    tips: ['Melt hips toward heels', 'Soften your shoulders', 'Breathe into the lower back', 'Release any tension in the jaw'],
    duration: 30,
    rest: 10,
    isCardio: false,
  },
  {
    id: 'downward-facing-dog',
    name: 'Downward-Facing Dog',
    description: 'Start on hands and knees. Tuck toes and lift hips high to form an inverted V. Press evenly through your palms, lengthen your spine, and gently pedal your heels toward the mat.',
    muscles: ['Hamstrings', 'Calves', 'Shoulders', 'Upper Back'],
    difficulty: 'Beginner',
    images: pose('downward-facing-dog'),
    tips: ['Press away from the mat', 'Long spine', 'Soften your knees if hamstrings are tight', 'Spread your fingers'],
    duration: 30,
    rest: 10,
    isCardio: false,
  },
  {
    id: 'warrior-i',
    name: 'Warrior I',
    description: 'Step one foot forward into a lunge. Square your hips forward and bend the front knee to 90°. Raise your arms overhead and gaze up. Hold for several breaths, then switch sides.',
    muscles: ['Quadriceps', 'Hip Flexors', 'Glutes', 'Shoulders'],
    difficulty: 'Beginner',
    images: pose('warrior-i'),
    tips: ['Square your hips', 'Front knee over ankle', 'Lift through the crown of the head', 'Energize your arms'],
    duration: 30,
    rest: 10,
    isCardio: false,
  },
  {
    id: 'warrior-ii',
    name: 'Warrior II',
    description: 'Stand with feet wide apart. Turn your front foot out 90° and back foot in slightly. Bend the front knee to 90° and extend arms out to the sides at shoulder height. Gaze over the front fingers.',
    muscles: ['Quadriceps', 'Glutes', 'Adductors', 'Shoulders'],
    difficulty: 'Beginner',
    images: pose('warrior-ii'),
    tips: ['Front knee tracks over second toe', 'Shoulders down', 'Gaze steady', 'Hold the hips low'],
    duration: 30,
    rest: 10,
    isCardio: false,
  },
  {
    id: 'tree-pose',
    name: 'Tree Pose',
    description: 'Stand on one foot. Place the sole of your other foot on your inner calf or inner thigh (not the knee). Bring palms together at your chest or raise arms overhead. Hold, then switch.',
    muscles: ['Glutes', 'Adductors', 'Core', 'Ankle Stabilizers'],
    difficulty: 'Beginner',
    images: pose('tree-pose'),
    tips: ['Rooted standing foot', 'Avoid pressing the foot into the knee', 'Soft gaze', 'Lift through the crown'],
    duration: 30,
    rest: 10,
    isCardio: false,
  },
  {
    id: 'cobra-pose',
    name: 'Cobra Pose',
    description: 'Lie face down with hands under your shoulders. Press through your palms to lift your chest, keeping your pelvis on the mat and elbows slightly soft. Hold, then lower slowly.',
    muscles: ['Lower Back', 'Glutes', 'Chest', 'Anterior Deltoid'],
    difficulty: 'Beginner',
    images: pose('cobra-pose'),
    tips: ['Elbows stay slightly bent', 'Shoulders away from ears', 'Pelvis stays grounded', 'Lengthen through the crown'],
    duration: 20,
    rest: 10,
    isCardio: false,
  },
  {
    id: 'bridge-pose',
    name: 'Bridge Pose',
    description: 'Lie on your back with knees bent and feet flat, hip-width apart. Press through your feet to lift your hips, clasping hands underneath you. Hold the pose while breathing steadily.',
    muscles: ['Glutes', 'Hamstrings', 'Core', 'Lower Back'],
    difficulty: 'Beginner',
    images: pose('bridge-pose'),
    tips: ['Press feet into the mat', 'Squeeze glutes', "Don't strain the neck", 'Breathe into the chest'],
    duration: 30,
    rest: 10,
    isCardio: false,
  },
  {
    id: 'seated-forward-fold',
    name: 'Seated Forward Fold',
    description: 'Sit with legs extended straight. Inhale to lengthen your spine, then exhale and hinge forward from your hips, reaching for your feet or shins. Hold, breathing into the stretch.',
    muscles: ['Hamstrings', 'Lower Back', 'Calves', 'Upper Back'],
    difficulty: 'Beginner',
    images: pose('seated-forward-fold'),
    tips: ['Hinge from hips not waist', 'Keep spine long', 'Soft knees if hamstrings are tight', 'Reach forward not down'],
    duration: 45,
    rest: 10,
    isCardio: false,
  },
  {
    id: 'savasana',
    name: 'Savasana',
    description: 'Lie flat on your back with arms slightly away from your sides, palms up. Let your feet fall open naturally. Close your eyes, relax every muscle, and breathe naturally.',
    muscles: [],
    difficulty: 'Beginner',
    images: pose('savasana'),
    tips: ['Release all muscular effort', 'Soften your face', 'Let the floor support you', 'Breathe naturally'],
    duration: 60,
    isCardio: false,
  },
];

const WORKOUT = {
  name: 'Morning Flow Yoga',
  description: 'A gentle full-body flow to wake up the spine, open the hips, and set a calm tone for the day.',
  modality: 'yoga',
  duration_minutes: 25,
  difficulty: 'Beginner',
};

const DIFFICULTY_COLOR: Record<string, string> = {
  Beginner:     '#4caf87',
  Intermediate: '#f5a623',
  Advanced:     '#e05252',
};

// ── Audio ─────────────────────────────────────────────────────────────────────

let _audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext | null {
  try {
    if (!_audioCtx || _audioCtx.state === 'closed') _audioCtx = new AudioContext();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
    return _audioCtx;
  } catch { return null; }
}

function tone(ctx: AudioContext, freq: number, start: number, dur: number, vol = 0.22) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(vol, start + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
  osc.start(start);
  osc.stop(start + dur + 0.05);
}

function playAlertSound() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  tone(ctx, 523, ctx.currentTime, 0.12);
}

function playCompleteSound() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const t = ctx.currentTime;
  tone(ctx, 523, t,        0.14);
  tone(ctx, 659, t + 0.14, 0.14);
  tone(ctx, 784, t + 0.28, 0.22);
}

// ── Countdown timer ───────────────────────────────────────────────────────────

function CountdownTimer({
  duration,
  size = 160,
  autoStart = true,
  onComplete,
}: {
  duration: number;
  size?: number;
  autoStart?: boolean;
  onComplete?: () => void;
}) {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [running, setRunning] = useState(autoStart);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => { setTimeLeft(duration); setRunning(autoStart); }, [duration, autoStart]);

  const prevRunningRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevRunningRef.current !== null) playAlertSound();
    prevRunningRef.current = running;
  }, [running]);

  useEffect(() => {
    if (!running || timeLeft <= 0) {
      if (timeLeft <= 0) { playCompleteSound(); onCompleteRef.current?.(); }
      return;
    }
    const t = setTimeout(() => setTimeLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, running]);

  const done = timeLeft === 0;
  const pristine = timeLeft === duration && !running;
  const r = (size - 12) / 2;
  const circumference = 2 * Math.PI * r;
  const progress = timeLeft / duration;
  const offset = circumference * (1 - progress);
  const ringColor = done ? '#4caf87' : !running && !pristine ? '#aaa'
    : progress > 0.5 ? '#4caf87' : progress > 0.25 ? '#f5a623' : '#e05252';

  return (
    <div className="wr-timer-wrapper">
      <div className="wr-timer" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#ebebeb" strokeWidth="8" />
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={ringColor} strokeWidth="8"
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
            transform={`rotate(-90 ${size/2} ${size/2})`}
            style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.4s ease' }} />
        </svg>
        <div className="wr-timer-text" style={{ color: ringColor }}>
          {done ? '✓' : `${timeLeft}`}
        </div>
      </div>
      {!done && (
        <button
          className={`wr-timer-control ${running ? 'wr-timer-control--pause' : 'wr-timer-control--start'}`}
          onClick={() => setRunning(r => !r)}
        >
          {running ? 'Pause' : pristine ? 'Start' : 'Resume'}
        </button>
      )}
    </div>
  );
}

// ── Animated image ────────────────────────────────────────────────────────────

function AnimatedExerciseImage({ images }: { images: string[] }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  if (images.length === 0) return null;

  const src = images[0];

  return (
    <div className="wr-image-frame">
      {!loaded && !errored && (
        <div style={{ fontSize: 13, color: '#bbb' }}>Loading...</div>
      )}
      {errored ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: '1rem' }}>
          <div style={{ fontSize: 13 }}>Image not available</div>
          <div style={{ fontSize: 11, marginTop: 4, wordBreak: 'break-all' }}>{src}</div>
        </div>
      ) : (
        <img
          src={src}
          alt="Exercise"
          className={`wr-exercise-img ${loaded ? 'wr-img-visible' : 'wr-img-hidden'}`}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
        />
      )}
    </div>
  );
}

// ── Screens ───────────────────────────────────────────────────────────────────

function OverviewScreen({ onStart }: { onStart: () => void }) {
  return (
    <div className="wr-screen wr-overview">
      <div className="wr-overview-header">
        <div className="wr-badge">Yoga Demo</div>
        <h1 className="wr-title">{WORKOUT.name}</h1>
        <p className="wr-subtitle">
          {YOGA_EXERCISES.length} poses &middot; ~{WORKOUT.duration_minutes} min &middot; {WORKOUT.difficulty}
        </p>
        <p style={{ color: '#888', fontSize: 13, marginTop: 8 }}>{WORKOUT.description}</p>
      </div>

      <div className="wr-exercise-list">
        {YOGA_EXERCISES.map((ex, idx) => (
          <div key={ex.id} className="wr-exercise-card">
            <div className="wr-card-num">{idx + 1}</div>
            <div className="wr-card-info">
              <div className="wr-card-name">{ex.name}</div>
              <div className="wr-card-meta">
                {ex.duration ? `${ex.duration}s hold` : 'Flow with breath'}
                {ex.rest ? ` · ${ex.rest}s rest` : ''}
              </div>
              {ex.muscles.length > 0 && (
                <div className="wr-card-muscles">{ex.muscles.slice(0, 3).join(', ')}</div>
              )}
            </div>
            <span className="wr-difficulty-badge" style={{
              background: DIFFICULTY_COLOR[ex.difficulty] + '22',
              color:      DIFFICULTY_COLOR[ex.difficulty],
            }}>
              {ex.difficulty}
            </span>
          </div>
        ))}
      </div>

      <button className="wr-btn-primary" onClick={onStart}>Get Started</button>
    </div>
  );
}

function ExerciseScreen({
  exercise,
  index,
  total,
  onComplete,
}: {
  exercise: Exercise;
  index: number;
  total: number;
  onComplete: () => void;
}) {
  const [timerDone, setTimerDone] = useState(false);
  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLast = index + 1 >= total;

  function handleTimerComplete() {
    setTimerDone(true);
    autoAdvanceRef.current = setTimeout(onComplete, 1500);
  }

  function handleManualComplete() {
    if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);
    onComplete();
  }

  useEffect(() => () => { if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current); }, []);

  return (
    <div className="wr-screen wr-exercise-screen">
      <div className="wr-progress-bar">
        <div className="wr-progress-fill" style={{ width: `${(index / total) * 100}%` }} />
      </div>
      <div className="wr-progress-label">Pose {index + 1} of {total}</div>

      <h2 className="wr-exercise-name">{exercise.name}</h2>
      {exercise.description && (
        <p className="wr-exercise-desc">{exercise.description}</p>
      )}

      {exercise.images.length > 0 && <AnimatedExerciseImage images={exercise.images} />}

      {exercise.duration ? (
        <div className="wr-timer-section">
          <CountdownTimer duration={exercise.duration} size={160} autoStart={false}
            onComplete={handleTimerComplete} />
          {timerDone && <div className="wr-timer-done-label">Nice work!</div>}
        </div>
      ) : (
        <div className="wr-stat-row">
          <div className="wr-stat">
            <span className="wr-stat-val">~</span>
            <span className="wr-stat-label">Flow</span>
          </div>
          <div className="wr-stat-divider" />
          <div className="wr-stat">
            <span className="wr-stat-val">Breath</span>
            <span className="wr-stat-label">Paced</span>
          </div>
        </div>
      )}

      {exercise.muscles.length > 0 && (
        <div className="wr-muscles">
          {exercise.muscles.map(m => (
            <span key={m} className="wr-muscle-chip">{m}</span>
          ))}
        </div>
      )}

      {exercise.tips.length > 0 && (
        <div className="wr-tips">
          <div className="wr-tips-header">Form Tips</div>
          <ul className="wr-tips-list">
            {exercise.tips.map((tip, i) => <li key={i}>{tip}</li>)}
          </ul>
        </div>
      )}

      <button className="wr-btn-primary wr-btn-complete" onClick={handleManualComplete}>
        {timerDone
          ? isLast ? 'Finish' : 'Next Pose'
          : exercise.duration
          ? 'Skip Timer'
          : isLast ? 'Finish' : 'Next Pose'}
      </button>
    </div>
  );
}

function RestScreen({
  seconds,
  nextExercise,
  onDone,
}: {
  seconds: number;
  nextExercise: Exercise;
  onDone: () => void;
}) {
  return (
    <div className="wr-screen wr-rest-screen">
      <div className="wr-rest-label">Rest</div>
      <CountdownTimer duration={seconds} size={180} onComplete={onDone} />
      <div className="wr-next-up">
        <div className="wr-next-label">Next up</div>
        <div className="wr-next-name">{nextExercise.name}</div>
        <div className="wr-next-meta">
          {nextExercise.duration ? `${nextExercise.duration}s hold` : 'Flow with breath'}
        </div>
      </div>
      <button className="wr-btn-secondary" onClick={onDone}>Skip Rest</button>
    </div>
  );
}

function DoneScreen({ onReset }: { onReset: () => void }) {
  return (
    <div className="wr-screen wr-done">
      <div className="wr-done-icon">&#10003;</div>
      <h1 className="wr-title">Great work!</h1>
      <p className="wr-subtitle">
        You completed all {YOGA_EXERCISES.length} poses. Take a moment to notice how you feel.
      </p>
      <div className="wr-done-stats">
        <div className="wr-stat">
          <span className="wr-stat-val">{YOGA_EXERCISES.length}</span>
          <span className="wr-stat-label">Poses</span>
        </div>
        <div className="wr-stat-divider" />
        <div className="wr-stat">
          <span className="wr-stat-val">&#10003;</span>
          <span className="wr-stat-label">Complete</span>
        </div>
      </div>
      <button className="wr-btn-secondary" onClick={onReset}>Start Over</button>
    </div>
  );
}

// ── Root component ────────────────────────────────────────────────────────────

type Phase = 'overview' | 'exercise' | 'rest' | 'done';

export default function YogaDemo() {
  const [phase, setPhase]           = useState<Phase>('overview');
  const [currentIdx, setCurrentIdx] = useState(0);

  function handleStart() { setCurrentIdx(0); setPhase('exercise'); }

  function handleExerciseComplete() {
    const hasNext = currentIdx + 1 < YOGA_EXERCISES.length;
    if (!hasNext) { setPhase('done'); return; }
    const restSeconds = YOGA_EXERCISES[currentIdx].rest;
    if (restSeconds && restSeconds > 0) {
      setPhase('rest');
    } else {
      setCurrentIdx(i => i + 1);
    }
  }

  function handleRestDone() { setCurrentIdx(i => i + 1); setPhase('exercise'); }
  function handleReset() { setCurrentIdx(0); setPhase('overview'); }

  return (
    <div className="wr-page">
      <PublicHeader />

      <main className="wr-root">
        <div className="wr-container">
          {phase === 'overview' && <OverviewScreen onStart={handleStart} />}
          {phase === 'exercise' && (
            <ExerciseScreen
              key={currentIdx}
              exercise={YOGA_EXERCISES[currentIdx]}
              index={currentIdx}
              total={YOGA_EXERCISES.length}
              onComplete={handleExerciseComplete}
            />
          )}
          {phase === 'rest' && currentIdx + 1 < YOGA_EXERCISES.length && (
            <RestScreen
              key={`rest-${currentIdx}`}
              seconds={YOGA_EXERCISES[currentIdx].rest!}
              nextExercise={YOGA_EXERCISES[currentIdx + 1]}
              onDone={handleRestDone}
            />
          )}
          {phase === 'done' && <DoneScreen onReset={handleReset} />}
        </div>
      </main>

      <footer className="wr-footer">
        <div className="wr-footer-inner">
          <div className="wr-footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="wr-footer-logo" />
            <span>mosaime</span>
          </div>
          <div className="wr-footer-links">
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="wr-footer-copy">&copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
